import { ReactNode, useCallback, useEffect, useState, lazy, Suspense } from 'react';
import { css, Global } from '@emotion/react';
import { FloatingNode, FloatingPortal, useFloatingNodeId } from '@floating-ui/react';
import { ErrorBoundaryProps, ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import {
  CreateTaskEventRequest,
  EventType,
} from '@weave/schema-gen-ts/dist/schemas/insys/onboarding/v1/onboarding-tasks/onboarding_tasks.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useIntakeFormShallowStore } from '@frontend/api-intake-form';
import { OnboardingModulesApi, OnboardingModulesTypes } from '@frontend/api-onboarding-modules';
import { SkipLinks } from '@frontend/accessibility';
import { getUser, hasSchemaACL, isWeaveUser } from '@frontend/auth-helpers';
import { BillingAlertGlobalBanner } from '@frontend/billing-alerts';
import { useVoicemailChannelSubscription, useCallInsightsChannelSubscription } from '@frontend/calls-v2';
import { PageFrame } from '@frontend/components';
import { GlobalClickToCallModal } from '@frontend/contact-actions-place-call';
import { DialpadManagerProvider } from '@frontend/dialpad';
import { ErrorBoundary, NavBarBoundary, TopBarBoundary, StackErrorPage } from '@frontend/error-boundary';
import { LargeDownloads, largeDownloadsRef } from '@frontend/file-download-helper';
import { GlobalBannerManager, GlobalBannerProvider } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { useInboxCallItemEventListener } from '@frontend/inbox-call-item-event-listener';
import { MessagingComplianceBanners } from '@frontend/messaging-compliance';
import { MiniChatManager, MobileMiniChatManager } from '@frontend/mini-chat';
import { useMiniChatErrorBoundaryHandlers } from '@frontend/mini-chat-store';
import {
  NotificationPreferencesProvider,
  NotificationProvider,
  NotificationQueue,
  FirestoreDbProvider,
  useNotificationQueue,
} from '@frontend/notifications';
import { PanelLayout, usePanelStore, PanelEnginePendoTrack } from '@frontend/panel-engine';
import { useCallPopDemo } from '@frontend/phone';
import { CallPopNotification } from '@frontend/pop';
import { breakpoints, useMatchMedia, useTargetElementContainerQuery } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useShellTheme } from '@frontend/shell-theme';
import { IpcShellProvider, shell } from '@frontend/shell-utils';
import { SyncAppAuthAlertBanner } from '@frontend/sync-app-auth';
import { TaskCenterProvider } from '@frontend/task-center';
import { TaskTraySidePanel } from '@frontend/task-tray';
import { TeamChatProvider, TeamChatComponents } from '@frontend/team-chat-new';
import { TourGuideComponent } from '@frontend/tour-guide';
import { getNavigationStartTime, sentry, SentrySpanMarker, pendo } from '@frontend/tracking';
import { usePreference, usePreferenceStore } from '@frontend/user-preferences';
import { WeaveLoader } from '@frontend/weave-loader';
import { useNavigate, useRouter, type AnyRoute } from '@frontend/weave-router';
import { EphemeralActiveBanner, EphemeralState } from '@frontend/weavemeral-banner';
import { WIEConnectPayersBanner } from '@frontend/wie-universal-banner';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, Text } from '@frontend/design-system';
import { GlobalNav, IntakeFormNav, NavContextProvider } from '../layout';
import NavProvider from '../layout/global-nav/nav/providers/nav-provider';
import { AppStatusProvider, useAppStatus } from '../providers/app-status.provider';
import { sharedQueryCache } from '../utils/query-cache';
import { WebsocketSubscriptionsProvider } from '../websocket-subscriptions/websocket-subscriptions-provider';
import { AnnouncementBanner } from './announcement-banner';
import { CommandPalette } from './command-palette';
import { GlobalPanelManager } from './global-banner-panels/global-panel-manager';
import { InfoModal } from './info-modal';
import { LocationPickerPage } from './location-pickers/location-picker-page';
import { NewUserOnboarding } from './new-user-onboarding';
import { useNewUserOnboardingStore } from './new-user-onboarding/store';
import { ResponsiveSlidePanel } from './responsive-slide-panel';
import { SalesDemoFeatureModal } from './sales-demo-feature-modal';
import { SettingsModal } from './settings/settings-modal';
import { SettingsGlobalPanelManager } from './settings-global-banner-panels/settings-global-panel-manager';
import { SimpleTopBar, TopBar } from './top-bar';
import { RibbonManager } from './top-bar-ribbons/ribbon-manager';
import { AppStateType, useInitializeApp } from './use-initialize-app';

const SherlockDevtools = lazy(() =>
  import('@frontend/sherlock').then((module) => ({ default: module.SherlockDevtools }))
);

const queryClient = new QueryClient({
  queryCache: sharedQueryCache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 1000 * 60 * 5,
    },
  },
});

type Props = {
  children: ReactNode;
};

export const AuthenticatedApp = ({ children }: Props) => {
  const weaveUser = isWeaveUser();
  const navigate = useNavigate();
  const appState = useInitializeApp();
  const { t } = useTranslation('base');
  const { getCustomizationFlagValue } = useScopedAppFlagStore();

  const user = getUser();
  const hasChatCustomizationFlag = getCustomizationFlagValue('chat');
  const hasActionBar = useMatchMedia({ minWidth: breakpoints.small.min });
  const [showReset, setShowReset] = useState(false);
  const shouldHaveChat = hasChatCustomizationFlag && user && !weaveUser;
  const { isShowIntakeForm } = useIntakeFormShallowStore('isShowIntakeForm');
  const [ExistingUser] = usePreference('ExistingUser', { omitDefaultValue: true });
  const { isNewUserOnboardingComplete } = useNewUserOnboardingStore('isNewUserOnboardingComplete');
  const { setShellColor } = useShellTheme('setShellColor');
  const isDataInitialized = usePreferenceStore((state) => state.isDataInitialized);
  const isDataError = usePreferenceStore((state) => state.isDataError);

  const [ephemeralState, setEphemeralState] = useState<EphemeralState>({
    isVisible: false,
    height: 0,
  });

  const pageHeadingJsx = (
    <>
      <EphemeralActiveBanner onChangeVisibility={setEphemeralState} />
    </>
  );

  useEffect(() => {
    if (appState.state === 'LOADING' || appState.state === 'REDIRECTING') {
      setTimeout(() => {
        setShowReset(true);
      }, 10000);
    } else {
      setShowReset(false);
    }
  }, [appState]);

  if (appState.state === 'LOADING' || appState.state === 'REDIRECTING') {
    return (
      <WeaveLoader
        containerCss={{
          flexDirection: 'column',
          gap: theme.spacing(2),
        }}
      >
        {showReset && (
          <>
            {pageHeadingJsx}
            <Text>Not loading? Try resetting the app.</Text>
            <Button
              size='large'
              variant='secondary'
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                window.location.replace('/sign-in');
              }}
            >
              Reset
            </Button>
          </>
        )}
      </WeaveLoader>
    );
  } else if (appState.state === 'ONBOARDING') {
    return <OnboardingRedirection redirectUrl={appState.meta.onboardingRedirectUrl} />;
  } else if (appState.state === 'LOCATION_SELECTION') {
    /**
     * Before this point, the actual app (Providers and content) has not been rendered yet.
     * Once we have initialized location data, we can proceed with rendering the app.
     *
     */
    if (shell.isShell && !ExistingUser && !isNewUserOnboardingComplete && isDataInitialized && !isDataError) {
      setShellColor('primaryDKGradient'); // Setting the default color until user sets a color
      const width = Math.min(window?.screen?.width * 0.8, 1500);
      shell?.invoke?.('main:update-options', { width }); // Set the shell width to a min of 80% of the screen width or 1500px, whichever is smaller
      pendo.disableGuides(); // Disable Pendo guides for new users until they complete onboarding
      return <NewUserOnboarding />;
    }

    return (
      <>
        <div
          css={{
            display: 'grid',
            gridTemplateRows: 'auto 1fr',
            overflow: 'auto',
          }}
        >
          <SimpleTopBar />
          {pageHeadingJsx}
          <LocationPickerPage />
        </div>
      </>
    );
  }

  /**
   * We wrap the Main content and Settings modal in a different query client provider so that we can use default settings for those components,
   * versus the one used in the top level of the app.
   *
   * The difference is that we turn on the error boundary for the top level components, but team-implemented pages will not trigger the error boundary from a React Query perspective.
   * This will change in the future, but we want to ensure that teams handle their errors before turning on the error boundary for all components.
   */

  return (
    <>
      <Global
        styles={{
          html: {
            overflow: 'hidden',
          },
        }}
      />
      <GlobalBannerProvider>
        <AppProviders>
          <TopBarContainer />
          <RibbonManager />
          <GlobalBannerSystem />
          <div
            id='main-container'
            style={{
              display: 'flex',
              overflow: 'hidden',
              height: '100%',
              position: 'relative',
            }}
          >
            <div
              id='app-container'
              style={{
                width: hasActionBar ? 'calc(100% - 57px)' : '100%',
                display: 'flex',
                overflow: 'hidden',
                height: '100%',
                position: 'relative',
              }}
            >
              <ErrorBoundary fallback={NavBarBoundary}>
                {isShowIntakeForm ? <IntakeFormNav /> : <GlobalNav />}
              </ErrorBoundary>
              <PageFrame>
                <QueryClientProvider client={queryClient}>
                  <Main
                    css={
                      ephemeralState.isVisible &&
                      css`
                        .ephemeral-warning-banner + * {
                          min-height: calc(100% - ${ephemeralState.height}px);
                          height: calc(100% - ${ephemeralState.height}px);
                        }
                      `
                    }
                  >
                    {appState.state === 'INITIALIZED' ? (
                      <SentryErrorBoundary
                        beforeCapture={(scope) => {
                          scope.setTag('topic', 'errorBoundary');
                          scope.setLevel('fatal');
                        }}
                        fallback={(props) => {
                          return (
                            <>
                              <StackErrorPage
                                error={props.error}
                                eventId={props.eventId}
                                componentStack={props.componentStack}
                                resetError={props.resetError}
                                primaryButtonText={t('Reload')}
                                onPrimaryButtonOnClick={() => window.location.reload()}
                                secondaryButtonText={t('Go to Home Page')}
                                onSecondaryButtonClick={() => navigate({ to: '/home/dashboard' as AnyRoute })}
                              />
                            </>
                          );
                        }}
                      >
                        {pageHeadingJsx}
                        {children}
                        <SentrySpanMarker
                          name='Weave app interactable'
                          op='ui.weaveapp.interactable'
                          startTime={getNavigationStartTime()}
                        />
                      </SentryErrorBoundary>
                    ) : null}
                  </Main>
                </QueryClientProvider>
              </PageFrame>
              <SentryErrorBoundary>
                <ResponsiveSlidePanel />
                <GlobalPanelManager />
                <SettingsGlobalPanelManager />
                <PanelLayout />
                <PanelEngineAutoClose />
                <PanelEnginePendoTrack />
              </SentryErrorBoundary>
              <SentryErrorBoundary>
                <TaskTraySidePanel />
                {shouldHaveChat ? <TeamChatComponents.ChatPanel /> : null}
              </SentryErrorBoundary>
            </div>
          </div>
          <QueryClientProvider client={queryClient}>
            <Settings appState={appState} />
          </QueryClientProvider>
        </AppProviders>
        <Miscellaneous />
      </GlobalBannerProvider>
    </>
  );
};

const OnboardingRedirection = ({ redirectUrl }: { redirectUrl: string }) => {
  const navigate = useNavigate();
  useEffect(() => {
    console.count('onboarding-redirect');
    //TODO: this is not ideal, but the router sync takes a moment, and if this navigation happens before the router sync
    //it can end up in a redirect loop. Once the legacy router sync is removed, we can remove this.
    setTimeout(() => {
      navigate({ to: redirectUrl as AnyRoute });
    }, 50);
  }, [navigate, redirectUrl]);
  return (
    <>
      <ContentLoader show />
    </>
  );
};

const TopBarContainer = () => {
  return (
    <SentryErrorBoundary fallback={TopBarBoundary}>
      <TopBar />
    </SentryErrorBoundary>
  );
};

const AppProviders = ({ children }: { children: ReactNode }) => {
  useVoicemailChannelSubscription();
  useCallInsightsChannelSubscription();
  // useHandleShortlinksOnAppLoad();
  useInboxCallItemEventListener({ source: 'callHistory' });

  const showTools = hasSchemaACL('weave', Permission.DEV_TOOLS) || import.meta.env.MODE === 'development';
  const user = getUser();

  return (
    <NotificationPreferencesProvider
      persistOptions={{
        name: `notification-preferences-${user?.userID}`,
        //@ts-ignore
        //TODO: Remove `ts-ignore` when we update to the latest version of `zustand`
        partialize: (state) => ({
          pausedNotificationTypes: state.pausedNotificationTypes,
          dismissedPreferences: state.dismissedPreferences,
        }),
      }}
    >
      <IpcShellProvider context='main'>
        <FirestoreDbProvider>
          <NotificationProvider>
            <GlobalClickToCallModal />
            <DialpadManagerProvider>
              <WebsocketSubscriptionsProvider>
                <AppStatusProvider>
                  <SkipLinks />
                  <NavProvider>
                    <NavContextProvider>
                      {/* Adding a provider here so that new chat panel could have a provider. But the old chat
                  provider continue using the ChatStrategyProvider. When we migrate to the new chat we will
                  remove the old chat strategy provider. Hence conditionally using the providers. */}
                      <TeamChatV2ProviderWithErrorBoundary>
                        <TaskCenterWrapper>{children}</TaskCenterWrapper>
                      </TeamChatV2ProviderWithErrorBoundary>
                    </NavContextProvider>
                  </NavProvider>
                </AppStatusProvider>
              </WebsocketSubscriptionsProvider>
            </DialpadManagerProvider>
            <SentryErrorBoundary>
              <NotificationGutter />
            </SentryErrorBoundary>
            {showTools && (
              <Suspense fallback={null}>
                <SherlockDevtools />
              </Suspense>
            )}
          </NotificationProvider>
        </FirestoreDbProvider>
      </IpcShellProvider>
    </NotificationPreferencesProvider>
  );
};

const Miscellaneous = () => {
  const showTools = hasSchemaACL('weave', Permission.DEV_TOOLS) || import.meta.env.MODE === 'development';

  const tourGuideActionHandler = useCallback(
    (eventInfo: CreateTaskEventRequest, taskType?: OnboardingModulesTypes.TaskType) => {
      const isCompleteTaskEvent = eventInfo.eventType === EventType.EVENT_COMPLETE_TASK;
      const isLocationBasedTask = taskType === OnboardingModulesTypes.TaskType.Location;

      if (isCompleteTaskEvent && isLocationBasedTask) {
        OnboardingModulesApi.markLocationTaskCompleted(
          eventInfo.taskId,
          OnboardingModulesTypes.TaskCompletionType.Modules
        );
      }

      OnboardingModulesApi.sendTaskEvent(eventInfo);
    },
    []
  );

  return (
    <>
      {showTools && <CommandPalette />}
      <LargeDownloads ref={largeDownloadsRef} />
      <InfoModal />
      <TourGuideComponent.TourGuide onAction={tourGuideActionHandler} />
      {<SalesDemoFeatureModal />}
    </>
  );
};

const Settings = ({ appState }: { appState: AppStateType }) => {
  return appState.state === 'INITIALIZED' ? <SettingsModal /> : null;
};

const Main = ({ children, className }: { children: ReactNode; className?: string }) => {
  useCallPopDemo();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const { onErrorBoundaryTriggered } = useMiniChatErrorBoundaryHandlers();
  const hasTrayPanels = usePanelStore((state) => state.hasTrayPanels);

  const handleMiniChatErrorScope = useCallback<NonNullable<ErrorBoundaryProps['beforeCapture']>>(
    (scope) => {
      onErrorBoundaryTriggered();
      scope.setTag('topic', 'messages');
      scope.setLevel('fatal');
      scope.setTag('feature', 'mini-chat');
    },
    [onErrorBoundaryTriggered]
  );

  return (
    <>
      <main
        id='main-content'
        tabIndex={0}
        css={css`
          container-name: main-content;
          container-type: size;
          width: 100%;
          overflow-y: auto;
          position: relative;

          // min-width: 0 prevents inner flex components from causing this to overflow its bounds
          // https://stackoverflow.com/questions/36230944/prevent-flex-items-from-overflowing-a-container
          min-width: 0;
          scrollbar-gutter: stable; // Prevent layout shift when scrollbar appear
        `}
        className={className}
      >
        {children}
        {/* Don't use a fallback here since it doesn't make sense in the UI */}
        <SentryErrorBoundary beforeCapture={handleMiniChatErrorScope}>
          {!isMobile && !hasTrayPanels && <MiniChatManager />}
        </SentryErrorBoundary>
      </main>
      {/* Don't use a fallback here since it doesn't make sense in the UI */}
      <SentryErrorBoundary beforeCapture={handleMiniChatErrorScope}>
        {(isMobile || hasTrayPanels) && <MobileMiniChatManager />}
      </SentryErrorBoundary>
    </>
  );
};

const NotificationGutter = () => {
  const { placement = 'top-right' } = useNotificationQueue();

  const nodeId = useFloatingNodeId();
  const [x, y] = placement.split('-');

  const [isMatch] = useTargetElementContainerQuery({
    maxWidth: breakpoints.xsmall.max,
    element: document.querySelector('body') as HTMLElement,
  });

  return (
    <FloatingNode id={nodeId}>
      <FloatingPortal>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            ...(x === 'top' ? { top: 73 } : { bottom: 0 }),
            ...(y === 'right' ? { right: isMatch ? 8 : 57 } : { left: 0 }),
            zIndex: theme.zIndex.alerts,
          }}
        >
          <CallPopNotification />
          <NotificationQueue />
        </div>
      </FloatingPortal>
    </FloatingNode>
  );
};

const TeamChatV2ProviderWithErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const hasChatCustomizationFlag = getCustomizationFlagValue('chat');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isThreadsSectionEnabled = getFeatureFlagValue('team-chat-threads-section');
  const shouldFetchConversationUsers = getFeatureFlagValue('team-chat-new-use-users-in-cache');
  const { selectedOrgId } = useAppScopeStore();
  const { teamChat: teamChatStatus } = useAppStatus();

  const user = getUser();
  const weaveUser = isWeaveUser();

  if (!hasChatCustomizationFlag || !user || weaveUser) {
    return <>{children}</>;
  }

  return (
    <ErrorBoundary
      key={teamChatStatus.key}
      onError={(error, componentStack, eventId) => {
        const errorMessage =
          error && typeof error === 'object' && 'toString' in error ? error.toString() : 'An error occurred';
        teamChatStatus.updateStatus('error', errorMessage);
        console.error('Error in TeamChatV2ProviderWithErrorBoundary', error, componentStack, eventId);
        sentry.error({
          error,
          team: 'fe-bizx',
          topic: 'team-chat',
        });
      }}
      fallback={<>{children}</>}
    >
      <TeamChatProvider
        api='stream'
        orgId={selectedOrgId}
        userId={user.userID}
        key={'team-chat-' + selectedOrgId}
        isThreadsSectionEnabled={isThreadsSectionEnabled}
        shouldFetchConversationUsers={shouldFetchConversationUsers}
      >
        {children}
      </TeamChatProvider>
    </ErrorBoundary>
  );
};

const GlobalBannerSystem = () => {
  const { isShowIntakeForm } = useIntakeFormShallowStore('isShowIntakeForm');

  return (
    <>
      <GlobalBannerManager />
      <AnnouncementBanner />
      <BillingAlertGlobalBanner />
      <SyncAppAuthAlertBanner />
      {!isShowIntakeForm && <MessagingComplianceBanners.BusinessInformationBanner />}
      <WIEConnectPayersBanner />
    </>
  );
};

export const TaskCenterWrapper = ({ children }: { children: React.ReactNode }) => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isTaskCenterAvailable = getFeatureFlagValue('new-task-center-experience');

  if (isTaskCenterAvailable) {
    return (
      <ErrorBoundary
        onError={(error, componentStack, eventId) => {
          console.error('Error in TaskCenterProvider', error, componentStack, eventId);
          sentry.error({
            error,
            team: 'fe-bizx',
            topic: 'task-center',
          });
        }}
        fallback={<>{children}</>}
      >
        <TaskCenterProvider>{children}</TaskCenterProvider>
      </ErrorBoundary>
    );
  }
  return children;
};

const PanelEngineAutoClose = () => {
  const router = useRouter();
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  useEffect(() => {
    const unsubscribe = router.subscribe('onLoad', () => {
      if (isXSmall) {
        usePanelStore.getState().closeAllPanels();
      }
    });

    return unsubscribe;
  }, [router, isXSmall]);

  return null;
};
